import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { apiClient } from "../../services/apiClient";
import LoadingSpinner from "../common/LoadingSpinner";
import ErrorMessage from "../common/ErrorMessage";

const RfpUploadModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [dragActive, setDragActive] = useState(false);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [error, setError] = useState("");

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    setError("");

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  }, []);

  const handleFileSelect = (selectedFile) => {
    // Validate file type
    const allowedTypes = ['.pdf', '.doc', '.docx'];
    const fileExtension = selectedFile.name.toLowerCase().substring(selectedFile.name.lastIndexOf('.'));

    if (!allowedTypes.includes(fileExtension)) {
      setError(`File type ${fileExtension} not supported. Please upload PDF, DOC, or DOCX files.`);
      return;
    }

    // Validate file size (50MB limit)
    const maxSize = 50 * 1024 * 1024; // 50MB in bytes
    if (selectedFile.size > maxSize) {
      setError("File size must be less than 50MB.");
      return;
    }

    setFile(selectedFile);
    setError("");
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const handleCreateManually = () => {
    onClose();
    navigate("/proposals/new");
  };

  const handleCreateFromRfp = async () => {
    if (!file) return;

    setUploading(true);
    setExtracting(true);
    setError("");

    try {
      const formData = new FormData();
      formData.append('rfpFile', file);

      const response = await apiClient.post('/api/proposals/extract-from-rfp', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      console.log("RFP extraction response:", response.data);

      // Check for successful response - handle the actual response structure
      if (response.data && response.data.extractedFields && response.data.documentId) {
        // Navigate to proposal form with extracted data
        onClose();
        navigate("/proposals/new", {
          state: {
            extractedData: response.data.extractedFields,
            rfpDocumentId: response.data.documentId,
            isFromRfpExtraction: true
          }
        });
      } else {
        console.error("Unexpected response structure:", response.data);
        throw new Error("Failed to extract proposal information - invalid response structure");
      }
    } catch (err) {
      console.error("RFP extraction error:", err);
      setError(
        err.response?.data?.message ||
        err.message ||
        "Failed to process RFP document. Please try again or create manually."
      );
    } finally {
      setUploading(false);
      setExtracting(false);
    }
  };

  const removeFile = () => {
    setFile(null);
    setError("");
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <div>
            <h2 className="text-xl font-semibold text-slate-900">Create New Proposal</h2>
            <p className="text-slate-600 mt-1">Upload an RFP document or create manually</p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 transition-colors"
            disabled={uploading}
          >
            <FontAwesomeIcon icon="times" className="text-xl" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {error && (
            <div className="mb-6">
              <ErrorMessage message={error} onRetry={() => setError("")} />
            </div>
          )}

          {extracting ? (
            /* Extraction in progress */
            <div className="text-center py-12">
              <LoadingSpinner size="lg" />
              <h3 className="text-lg font-medium text-slate-900 mt-4">Processing RFP Document</h3>
              <p className="text-slate-600 mt-2">
                AI is extracting proposal information from your document...
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Manual Creation Option */}
              <div className="space-y-4">
                <div className="text-center">
                  <div className="w-16 h-16 mx-auto bg-slate-100 rounded-full flex items-center justify-center mb-4">
                    <FontAwesomeIcon icon="edit" className="text-slate-600 text-2xl" />
                  </div>
                  <h3 className="text-lg font-medium text-slate-900 mb-2">Create Manually</h3>
                  <p className="text-slate-600 text-sm mb-4">
                    Fill out the proposal form with your own information
                  </p>
                  <button
                    onClick={handleCreateManually}
                    className="w-full px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors duration-200"
                    disabled={uploading}
                  >
                    Create Manually
                  </button>
                </div>
              </div>

              {/* RFP Upload Option */}
              <div className="space-y-4">
                <div className="text-center">
                  <div className="w-16 h-16 mx-auto bg-primary-100 rounded-full flex items-center justify-center mb-4">
                    <FontAwesomeIcon icon="robot" className="text-primary-600 text-2xl" />
                  </div>
                  <h3 className="text-lg font-medium text-slate-900 mb-2">Upload RFP Document</h3>
                  <p className="text-slate-600 text-sm mb-4">
                    Let AI extract information from your RFP automatically
                  </p>
                </div>

                {/* File Upload Area */}
                <div
                  className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors duration-200 ${
                    dragActive
                      ? "border-primary-400 bg-primary-50"
                      : file
                      ? "border-green-400 bg-green-50"
                      : "border-slate-300 hover:border-slate-400"
                  }`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                >
                  {file ? (
                    /* File Selected */
                    <div className="space-y-3">
                      <FontAwesomeIcon icon="file-alt" className="text-green-600 text-3xl" />
                      <div>
                        <p className="font-medium text-slate-900">{file.name}</p>
                        <p className="text-sm text-slate-600">
                          {(file.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                      <button
                        onClick={removeFile}
                        className="text-sm text-red-600 hover:text-red-700"
                        disabled={uploading}
                      >
                        Remove file
                      </button>
                    </div>
                  ) : (
                    /* No File Selected */
                    <div className="space-y-3">
                      <FontAwesomeIcon
                        icon="upload"
                        className={`text-3xl ${dragActive ? "text-primary-600" : "text-slate-400"}`}
                      />
                      <div>
                        <p className="text-slate-900 font-medium">
                          Drop your RFP document here
                        </p>
                        <p className="text-sm text-slate-600">
                          or click to browse
                        </p>
                        <p className="text-xs text-slate-500 mt-2">
                          Supports PDF, DOC, DOCX (max 50MB)
                        </p>
                      </div>
                      <input
                        type="file"
                        onChange={handleFileInput}
                        accept=".pdf,.doc,.docx"
                        className="hidden"
                        id="rfp-file-input"
                        disabled={uploading}
                      />
                      <label
                        htmlFor="rfp-file-input"
                        className="inline-block px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-md cursor-pointer transition-colors duration-200"
                      >
                        Browse Files
                      </label>
                    </div>
                  )}
                </div>

                {/* Create Button */}
                <button
                  onClick={handleCreateFromRfp}
                  disabled={!file || uploading}
                  className="w-full px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {uploading ? (
                    <>
                      <FontAwesomeIcon icon="spinner" className="animate-spin" />
                      <span>Processing...</span>
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon="magic" />
                      <span>Create with AI</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {!extracting && (
          <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 rounded-b-xl">
            <p className="text-xs text-slate-600 text-center">
              AI-extracted information can be reviewed and edited before creating the proposal
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RfpUploadModal;